import {
  BookingSourceType,
  BookingState,
  Gym,
  IEnabledFeatures,
  IExternalUserReference,
  ILiveStreamOptions,
  WorkoutType,
} from 'fitbeat.models';
import { AreaCategory } from '../../config/zone-mappings';
import {
  IExternalReference,
  IGymSchedule,
  IUserWorkoutSessionProfile,
} from '../../entities';
import {
  IDisplayableUserProfile,
  UserProfile,
  WorkoutSettings,
} from '../../entities/user';
import { UserWorkoutSession } from '../../entities/workoutsession';
import { IDisplayableOpenInviteDetails } from '../../entities/workoutsession/openinvites';
import { IBaseTimeSlotWithAvailability, ITimeSlot, ITimeslotCapacity } from '../gymscheduling/igymscheduling';
import { Waitlist } from '../waitlist/waitlist';
import { GymTimeSlotsManager } from './gymtimeslotsmanager';
import { IAvailableStudioTimeSlots } from './select-studio-class-time';

export interface IBookingRequest {
  user: string | UserProfile;
  enabledFeatures: IEnabledFeatures;
  startTime: Date;
  requestTime: Date;
  workoutType: WorkoutType;
  bookingSource: BookingSourceType;
  allowSameDay?: boolean;
  ptOverride?: boolean;
  station?: number;
  gym?: Gym;
  isInstant?: boolean;
  isDemo?: boolean;
  timezone?: string;
  workoutSettings?: WorkoutSettings;
  isStudioDemoBooking?: boolean;
  playbackSpeed?: number;
  externalBookingReference?: IExternalReference;
  isHomeGroupWorkout?: boolean;
  livestreamOptions?: ILiveStreamOptions;
  isStreamingWorkout?: boolean;
  pairedSessionReference?: string;
  pairedSessionUserProfileId?: string;
  areaCategory?: AreaCategory;
}

export interface IUserSessionHistoryEntry {
  userId: string;
  firstExerciseTime: Date;
  bookingState: BookingState;
  isPtApproved: boolean;
  checkedIn: boolean;
  cancelled: boolean;
  userExternalReference?: IExternalUserReference;
  sessionExternalReference?: IExternalReference;
}

export interface ICalculateTimeSlotsOptions {
  withPartner: boolean;
  onlyPeakTimeslots: boolean;
}

export interface IBookingsManager {
  GetCapacity(from?: Date, to?: Date, dateOfRequest?: Date, gym?: Gym): Promise<ITimeslotCapacity[]>;
  GetAvailableBookingSlots(
    withPartner: boolean,
    userProfile: UserProfile,
    gym?: Gym,
    from?: Date,
    to?: Date,
    dateOfRequest?: Date,
    checkDemerits?: boolean,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    allowEvenLaterLateBookings?: boolean,
  ): Promise<ITimeSlot[]>;
  getAmountOfOpenSlotsAtPoint(
    start: Date,
    end: Date,
    dateOfRequest: Date,
    gym: Gym,
    workoutType: WorkoutType,
    findFullZones: boolean,
  ): Promise<number>;
  getAmountOfOpenSlots(
    from: Date,
    to: Date,
    gym: Gym,
    dateOfRequest: Date,
    options?: Partial<ICalculateTimeSlotsOptions>,
  ): Promise<IBaseTimeSlotWithAvailability[]>;
  PTBookSingle(
    userEmail: string,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    allowSameDay: boolean,
    station: number,
    gym: Gym,
    workoutSettings: WorkoutSettings,
  ): Promise<UserWorkoutSession>;
  // todo: can generalize IBookingRequest to allow this to call other booking functions (and not just single)
  BookSingleFromParameters(request: IBookingRequest): Promise<UserWorkoutSession>;
  BookSingle(
    userId: string | UserProfile,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    bookingSource: BookingSourceType,
    allowSameDay?: boolean,
    ptOverride?: boolean,
    station?: number,
    gym?: Gym,
    isInstant?: boolean,
    isDemo?: boolean,
    timezone?: string,
    workoutSettings?: WorkoutSettings,
    isStudioDemoBooking?: boolean,
    playbackSpeed?: number,
    externalBookingReference?: IExternalReference,
  ): Promise<UserWorkoutSession>;
  BookWithPartner(
    userId: string,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    bookingSource: string,
    allowSameDay?: boolean,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    gym?: Gym,
    workoutSettings?: WorkoutSettings,
    externalBookingReference?: IExternalReference,
  ): Promise<UserWorkoutSession>;
  InviteAndBookWithPartner(
    partnerId: string,
    userId: string,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    allowSameDay: boolean,
    gym: Gym,
    bookingSource: string,
    workoutSettings: WorkoutSettings,
  ): Promise<UserWorkoutSession>;
  CancelBooking(userId: string, workoutId: string, dateOfRequest?: Date, sessionsFromNow?: number): Promise<void>;
  CancelBookingByExternalReference(reference: IExternalReference): Promise<void>;
  UndoCancel(userId: string, workoutId: string, dateOfRequest?: Date): Promise<UserWorkoutSession>;
  ChangeBooking(
    userId: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    toDate: Date,
    changeToWorkoutType: WorkoutType,
    dateOfChange: Date,
    gym: Gym): Promise<UserWorkoutSession>;
  PTCheckIn(gym: Gym, userId: string, checkInTime: Date): Promise<UserWorkoutSession>;
  CheckIn(
    gym: Gym,
    userId: string,
    checkInTime: Date,
    allowBagContact: boolean,
    skippedBiometric?: boolean,
  ): Promise<UserWorkoutSession>;
  CheckInUserWorkoutSession(workoutSessionId: string, checkInTime: Date): Promise<UserWorkoutSession>;
  InvitePartnerToBooking(
    userId: string,
    partnerUserId: string,
    provisionalWorkoutId: string,
    enabledFeatures: IEnabledFeatures,
    dateOfRequest?: Date,
    areaCategory?: AreaCategory,
  ): Promise<UserWorkoutSession>;
  SearchForPartnerToInviteToWorkout(
    usernameOrFullnameOrNickname: string,
    userId: string,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    gym: Gym,
    dateOfRequest?: Date,
  ): Promise<IDisplayableUserProfile[]>; // todo need to return a list of booking partner names-ids-statusses

  AvailabilitySearchForWhitelistedPartners(
    userId: string,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    gym: Gym,
    dateOfRequest?: Date): Promise<IDisplayableUserProfile[]>;

  AcceptInvite(
    userId: string,
    provisionalWorkoutId: string,
    acceptInviterSettings: boolean,
    dateOfRequest: Date,
    enabledFeatures: IEnabledFeatures,
  ): Promise<UserWorkoutSession>;
  CancelInvite(userId: string, workoutId: string, dateOfRequest: Date, sessionsFromNow?: number): Promise<void>;
  RejectInvite(userId: string, provisionalWorkoutId: string, dateOfRequest?: Date): Promise<void>;
  isAvailableForBooking(
    user: UserProfile,
    startTime: Date,
  ): Promise<boolean>;
  getTargetTimeSlot(
    user: UserProfile,
    withPartner: boolean,
    workoutType: WorkoutType,
    time: Date,
    dateOfRequest: Date,
    gym: Gym,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    allowEvenLaterLateBookings?: boolean,
  ): Promise<ITimeSlot | undefined>;
  setWaitList(waitlist: Waitlist): void;
  NotifyExpiredSessions(dateToCheckFor: Date, dateOfRequest: Date): Promise<UserWorkoutSession[]>;
  getGymTimeSlotsManager(): GymTimeSlotsManager;
  BookSingleInWholeZone(
    userId: string | UserProfile,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    gym: Gym,
    bookingSource: string,
    workoutSettings?: WorkoutSettings,
    externalBookingReference?: IExternalReference,
  ): Promise<UserWorkoutSession>;
  ChangeBookingInWholeZone(
    uid: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    changeToStartDate: Date,
    changeToWorkoutType: WorkoutType,
    requestTime: Date,
    gym: Gym,
    bookingSource: string,
    WorkoutSettings?: WorkoutSettings,
  ): Promise<UserWorkoutSession>;
  AcceptOpenInvite(
    userId: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    requestTime: Date,
  ): Promise<UserWorkoutSession>;
  GetOpenInviteBookingDetails(
    userId: string,
    workoutId: string,
    requestTime: Date,
  ): Promise<IDisplayableOpenInviteDetails>;
  SetInviterOpenInviteLink(
    userId: string,
    openInviteLink: string,
  ): Promise<UserWorkoutSession>;
  GetOpenInviteClassList(
    userId: string,
    workoutId: string,
    requestTime: Date,
  ): Promise<IDisplayableUserProfile[]>;
  GetOpenInviteClassWithWorkoutSettings(
    workout: UserWorkoutSession,
    requestTime: Date,
  ): Promise<IUserWorkoutSessionProfile[]>;
  GetGymSessionWorkoutInfo(
    gym: Gym, start: Date, workoutType: WorkoutType,
  ): Promise<IUserSessionHistoryEntry[]>;
  getTimeSlotsForUserInGym(
    userId: string,
    gymName: string,
    yyyymmdd: string,
    withPartner: boolean,
    requestTime: Date,
    returnSlotsIfUserHasBookingOnDay: boolean,
    useDeprecatedGoalNotAlignedError: boolean,
  ): Promise<IAvailableStudioTimeSlots[]>
}
